import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { productRowKey } from '@/lib/revenue-matrix';

/**
 * The stateful half of `matrix-visibility.ts`: one hidden set per scope, read
 * from and saved to `preferences.columns.<scope>.hidden`.
 *
 * ⚠️ **MOUNT IT ONCE PER PANEL, NOT PER MATRIX.** Every company (or product)
 * expanded in a panel reads the same instance, so a year hidden in one matrix
 * disappears from the matrix open beside it at once — two instances of one
 * scope would each hold their own copy and disagree until a remount.
 *
 * ── SAVING ────────────────────────────────────────────────────────────────
 *
 * - **Debounced** (`SAVE_DELAY_MS`), so ticking five years is one write.
 * - **One write in the air at a time**; a change made meanwhile is sent after
 *   it, so the last change is the last to arrive (each write carries the whole
 *   set, the race `use-column-layout` records).
 * - **A failed save keeps the local choice** and raises `saveFailed`, which the
 *   table renders as a danger notice; the next change tries again.
 * - **A pending save is flushed with `keepalive`** on unmount and on `pagehide`,
 *   so a choice made just before navigating away is not lost.
 * - **Nothing is accepted before the stored set is read** (`loading`), or a
 *   change made against the empty default would be overwritten on screen by the
 *   read — and would already have been saved.
 *
 * ⚠️ **A FAILED READ DEGRADES TO "NOTHING HIDDEN" and unlocks the controls**:
 * the figures are the page, the arrangement is chrome.
 *
 * The body names `hidden` alone. These scopes are written by nothing else, so
 * the recursive merge has no other part to leave behind; an empty set deletes
 * the scope branch rather than storing an empty array.
 */

const SAVE_DELAY_MS = 600;

export interface MatrixVisibility {
  /** Every stored hidden id, including ones no matrix currently renders. */
  hidden: ReadonlySet<string>;
  /** True until the stored set has been read; controls show as loading. */
  loading: boolean;
  /** True after a save failed and before the next one succeeds. */
  saveFailed: boolean;
  /** Hide or show several ids in ONE change (and one write). */
  setHidden: (keys: readonly string[], hide: boolean) => void;
}

/** The read every mounted scope shares while it is in the air — never a cache. */
let inFlight: Promise<unknown> | null = null;

function readPreferences(): Promise<unknown> {
  if (inFlight !== null) return inFlight;
  const request = api
    .getPreferences()
    .then((response) => response.data.preferences as unknown)
    .finally(() => {
      inFlight = null;
    });
  inFlight = request;
  return request;
}

function readHidden(preferences: unknown, scope: string): readonly string[] {
  if (preferences === null || typeof preferences !== 'object') return [];
  const columns = (preferences as Record<string, unknown>).columns;
  if (columns === null || typeof columns !== 'object') return [];
  const branch = (columns as Record<string, unknown>)[scope];
  if (branch === null || typeof branch !== 'object') return [];
  const hidden = (branch as Record<string, unknown>).hidden;
  return Array.isArray(hidden)
    ? hidden.filter((id): id is string => typeof id === 'string' && id.length > 0)
    : [];
}

function bodyFor(scope: string, hidden: ReadonlySet<string>) {
  return {
    preferences: {
      columns: { [scope]: hidden.size === 0 ? null : { hidden: [...hidden].sort() } },
    },
  };
}

export function useMatrixVisibility(scope: string): MatrixVisibility {
  const [hidden, setHiddenState] = useState<ReadonlySet<string>>(() => new Set());
  const [loading, setLoading] = useState(true);
  const [saveFailed, setSaveFailed] = useState(false);

  const loaded = useRef(false);
  const latest = useRef<ReadonlySet<string>>(hidden);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saving = useRef(false);
  const again = useRef(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    let cancelled = false;
    // `loading` starts true; a panel's scope is a module constant and never changes.
    loaded.current = false;
    readPreferences()
      .then((preferences) => {
        if (cancelled) return;
        const stored = new Set(readHidden(preferences, scope));
        latest.current = stored;
        setHiddenState(stored);
      })
      .catch(() => {
        // Degrade to nothing hidden — see the header.
      })
      .finally(() => {
        if (cancelled) return;
        loaded.current = true;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [scope]);

  const send = useCallback(async () => {
    if (saving.current) {
      again.current = true;
      return;
    }
    saving.current = true;
    try {
      do {
        again.current = false;
        try {
          await api.updatePreferences(bodyFor(scope, latest.current), undefined, true);
          if (mounted.current && !again.current) setSaveFailed(false);
        } catch {
          if (mounted.current) setSaveFailed(true);
        }
      } while (again.current);
    } finally {
      saving.current = false;
    }
  }, [scope]);

  // Flush a pending save on unmount and when the page is going away.
  useEffect(() => {
    const flush = () => {
      if (timer.current === null) return;
      clearTimeout(timer.current);
      timer.current = null;
      void api.updatePreferences(bodyFor(scope, latest.current), undefined, true).catch(() => {});
    };
    window.addEventListener('pagehide', flush);
    return () => {
      window.removeEventListener('pagehide', flush);
      mounted.current = false;
      flush();
    };
  }, [scope]);

  const setHidden = useCallback(
    (keys: readonly string[], hide: boolean) => {
      if (!loaded.current || keys.length === 0) return;
      const next = new Set(latest.current);
      let changed = false;
      for (const key of keys) {
        if (hide ? !next.has(key) : next.has(key)) {
          if (hide) next.add(key);
          else next.delete(key);
          changed = true;
        }
      }
      if (!changed) return;
      latest.current = next;
      setHiddenState(next);
      if (timer.current !== null) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        timer.current = null;
        void send();
      }, SAVE_DELAY_MS);
    },
    [send],
  );

  return useMemo(
    () => ({ hidden, loading, saveFailed, setHidden }),
    [hidden, loading, saveFailed, setHidden],
  );
}

/**
 * The row keys of every catalogued product flagged `isAddOn`, read from the
 * products catalogue — `null` while loading or when the read failed (the
 * switch is then disabled with its reason; nothing is guessed from a name).
 *
 * One catalogue read per mounting panel; the matrix endpoints carry no
 * add-on flag, and the catalogue is small (~30 rows) and already readable by
 * everyone who can open these dashboards.
 */
export function useAddOnProductRowKeys(): {
  keys: ReadonlySet<string> | null;
  failed: boolean;
} {
  const [state, setState] = useState<{ keys: ReadonlySet<string> | null; failed: boolean }>({
    keys: null,
    failed: false,
  });
  useEffect(() => {
    const controller = new AbortController();
    api
      .listAllProducts(controller.signal)
      .then((products) => {
        if (controller.signal.aborted) return;
        setState({
          keys: new Set(
            products.filter((product) => product.isAddOn).map((product) => productRowKey(String(product.id))),
          ),
          failed: false,
        });
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setState({ keys: null, failed: true });
      });
    return () => controller.abort();
  }, []);
  return state;
}
